import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { OAuthButtons } from "@/components/oauth-buttons";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useLogin } from "@/lib/api";
import {
  consumeSignupReturnTo,
  isSafeInternalPath,
  resolveAuthRedirect,
} from "@/lib/auth-redirect";
import { logger } from "@/lib/logger";

// Constant-length JSX expression keeps prettier output stable for any rendered name.
const PROJECT_NAME = "AI Disruption Exposure Index";

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = isSafeInternalPath(searchParams.get("returnTo"))
    ? searchParams.get("returnTo")
    : null;
  const loginMutation = useLogin();
  const [email, setEmail] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    logger.debug("[login] submitting for", email);
    try {
      const result = await loginMutation.mutateAsync({ email, password, rememberMe });
      // An email-verification link re-enters on /login with no query string, so
      // a returning invitee has no ?returnTo. Fall back to the browser-local
      // stash signup.tsx left before /verify-email (safe + within TTL), which
      // recovers e.g. /report/invite/… when the same browser opened the email.
      // Query param still wins when present (e.g. "Sign in" from a gated page).
      const target = returnTo ?? consumeSignupReturnTo();
      // target → mid-submission draft → role-based home (shared with OAuth).
      const destination = resolveAuthRedirect({ role: result?.role, returnTo: target });
      logger.debug("[login] success → redirecting to", destination, result);
      navigate(destination);
    } catch (err) {
      const status = (err as { status?: number } | undefined)?.status;
      const detail = (err as { body?: { detail?: string } } | undefined)?.body?.detail;
      if (status === 403 && detail === "email_not_verified") {
        // Preserve returnTo across the verification round-trip where we can; the
        // submit-pending draft is the fallback when the round-trip drops it.
        const verifyUrl = `/verify-email?email=${encodeURIComponent(email)}${
          returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ""
        }`;
        navigate(verifyUrl);
        return;
      }
      logger.debug("[login] failed:", err);
      setError(status === 401 ? "Invalid credentials." : "Sign-in failed. Please try again.");
    }
  };

  return (
    <div className="bg-background flex min-h-screen flex-col items-center justify-center px-4 py-10">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Link to="/" aria-label="Back to home" className="mx-auto mb-6 block w-fit">
            <img src="/brand/boi-logo-black.svg" alt="Board of Innovation" className="h-8 w-auto" />
          </Link>
          <CardTitle className="text-primary text-3xl font-normal tracking-tight">
            {PROJECT_NAME}
          </CardTitle>
          <p className="text-muted text-sm">
            Assess where AI-driven value erosion is likely to surface first.
          </p>
        </CardHeader>
        <CardContent>
          <OAuthButtons returnTo={returnTo ?? undefined} />
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-foreground text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="text-foreground text-sm font-medium">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-muted hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="rememberMe"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked === true)}
              />
              <label htmlFor="rememberMe" className="text-foreground text-sm">
                Remember me for 30 days
              </label>
            </div>
            {error && <p className="text-destructive text-sm">{error}</p>}
            <Button type="submit" disabled={loginMutation.isPending} className="w-full">
              {loginMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </Button>
            <p className="text-muted text-center text-sm">
              Need access to the index?{" "}
              <Link
                to={returnTo ? `/signup?returnTo=${encodeURIComponent(returnTo)}` : "/signup"}
                className="text-accent hover:underline"
              >
                Request an account
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
      <Link to="/" className="text-muted hover:text-primary mt-6 text-sm transition-colors">
        ← Back to home
      </Link>
    </div>
  );
}
